import React, { useState } from "react";
import { RotateCcw, ArrowRight, Music } from "lucide-react";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { StemDirectionRound } from "@/components/StemDirectionRound";
import { Stave, viewBox, ClefCtx } from "@/components/QuaverDraw";

const ROUND = 10;
const RIGHT = { up: { side: 1, tail: 1, curl: 1 }, down: { side: -1, tail: 1, curl: 1 } };
const WHY = {
  side: "Stem up goes on the RIGHT of the note; stem down goes on the LEFT.",
  tail: "A single quaver's tail is always on the RIGHT, whichever way the stem points.",
  curl: "The tail curls back down (or up) towards the note, like a little flag.",
};
const WRONGS = { up: [["side"], ["tail"], ["curl"], ["side", "tail"]], down: [["side"], ["tail"], ["curl"]] };

const Quaver = ({ q, clef }) => {
  const up = q.dir === "up", cy = up ? 96 : 72, cx = 50, sx = cx + 7 * q.side, tip = up ? cy - 34 : cy + 34, d = q.tail;
  const flag = up ? (q.curl > 0 ? `q ${4 * d} 10 ${10 * d} 16` : `q ${4 * d} -6 ${10 * d} -12`)
    : (q.curl > 0 ? `q ${6 * d} -8 ${10 * d} -18` : `q ${6 * d} 8 ${10 * d} 14`);
  return (
    <svg viewBox={viewBox(0, 30, 100, 110, clef)} className="mx-auto h-auto w-full max-w-[8rem] text-foreground sm:max-w-[9rem]" role="img" aria-label={`A quaver with the stem ${q.dir}`}>
      <ClefCtx.Provider value={clef}><Stave /></ClefCtx.Provider>
      <ellipse cx={cx} cy={cy} rx="7.5" ry="5.4" transform={`rotate(-20 ${cx} ${cy})`} fill="currentColor" />
      <line x1={sx} y1={cy} x2={sx} y2={tip} stroke="currentColor" strokeWidth="1.8" />
      <path d={`M ${sx} ${tip} ${flag}`} fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
};

const makeQuestion = () => {
  const dir = Math.random() < 0.5 ? "up" : "down";
  const wrongs = [...WRONGS[dir]].sort(() => Math.random() - 0.5).slice(0, 2).map((faults) => ({
    dir, faults, ...RIGHT[dir], ...Object.fromEntries(faults.map((f) => [f, -RIGHT[dir][f]])),
  }));
  return { dir, options: [{ dir, faults: [], ...RIGHT[dir] }, ...wrongs].sort(() => Math.random() - 0.5) };
};

// Lesson 4: spot the quaver whose stem and tail are drawn correctly.
export const QuaverStemQuiz = ({ testId = "quaver-quiz", clef = "treble" }) => {
  const bass = clef === "bass";
  const [q, setQ] = useState(makeQuestion);
  const [n, setN] = useState(1);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);
  const pick = (i) => {
    if (picked !== null) return;
    const ok = q.options[i].faults.length === 0;
    setPicked(i);
    playResult(ok);
    if (ok) setScore((s) => s + 1);
  };
  const next = () => {
    if (n === ROUND) {
      setDone(true);
      if (score === ROUND) playFanfare(0.2, bass ? "Quaver stem quiz (bass clef)" : "Quaver stem quiz");
      return;
    }
    setQ(makeQuestion()); setN(n + 1); setPicked(null);
  };
  const restart = () => { setQ(makeQuestion()); setN(1); setScore(0); setPicked(null); setDone(false); };
  const chosen = picked !== null ? q.options[picked] : null;
  const [level, setLevel] = useState(1);
  const tab = (l, label) => (
    <button type="button" onClick={() => setLevel(l)} data-testid={`${testId}-level-${l}`} aria-pressed={level === l}
      className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${level === l ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{label}</button>
  );

  return (
    <div className="mt-10 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Music size={14} /> Quaver stem quiz{bass ? " · bass clef" : ""}</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        {level === 1 && <p className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>{done ? `${ROUND} of ${ROUND}` : `Question ${n} of ${ROUND}`} · Score {score}</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">{tab(1, "Level 1 · stem & tail shape")}{tab(2, "Level 2 · which way does the stem point?")}</div>
      {level === 2 ? <StemDirectionRound testId={`${testId}-l2`} clef={clef} /> : done ? (
        <div className="mt-4 text-center">
          <p className="text-base font-semibold" data-testid={`${testId}-result`}>{score === ROUND ? `Perfect! ${score} out of ${ROUND} — you earned a star.` : `You scored ${score} out of ${ROUND}.`}</p>
          <button type="button" onClick={restart} data-testid={`${testId}-restart`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><RotateCcw size={14} /> Play again</button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm font-semibold">Which quaver is drawn correctly? <span className="font-normal text-muted-foreground">(stem {q.dir})</span></p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
            {q.options.map((o, i) => {
              const ok = o.faults.length === 0;
              const state = picked === null ? "border-border hover:border-primary" : ok ? "border-emerald-500 bg-emerald-500/10" : picked === i ? "border-red-500 bg-red-500/10" : "border-border opacity-60";
              return (
                <button key={i} type="button" onClick={() => pick(i)} disabled={picked !== null} data-testid={`${testId}-option-${i}`} data-correct={ok}
                  className={`rounded-xl border-2 bg-background/60 p-2 transition-colors ${state}`}>
                  <Quaver q={o} clef={clef} />
                </button>
              );
            })}
          </div>
          {chosen && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid={`${testId}-feedback`}>
              <p className={`text-sm font-semibold ${chosen.faults.length ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                {chosen.faults.length ? `Not quite. ${WHY[chosen.faults[0]]}` : `Correct! ${q.dir === "up" ? "Stem up on the right" : "Stem down on the left"}, tail on the right.`}
              </p>
              <button type="button" onClick={next} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">
                {n === ROUND ? "See my score" : "Next"} <ArrowRight size={13} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
